import { Injectable } from "@angular/core";
import { IAttachmentDto } from "src/gyzmo-commons/interfaces/IAttachmentDto";
import { AttachmentFactory } from "../../gyzmo-commons/interfaces/attachmentFactory";
import { AttachmentKinds } from "../../gyzmo-commons/interfaces/attachmentKinds";
import { DateProvider } from "../../gyzmo-commons/interfaces/dateProvider";
import { IDocumentsProvider } from "../../gyzmo-commons/interfaces/IDocumentsProvider";
import { CacheService } from "../../gyzmo-commons/services/cache.service";
import { AttachmentWsDao } from "../dao/ws/attachment.ws.dao";
import { AttachmentDto } from "../dto/attachment.dto";
import { ServersConnectionsProvider } from "../providers/serversConnections.provider";

export class AppAttachmentFactory extends AttachmentFactory {
    createAttachment(): IAttachmentDto {
        return new AttachmentDto();
    }
}

@Injectable({
    providedIn: "root",
})
export class AttachmentService extends IDocumentsProvider {
    private readonly DEFAULT_USER_MANUAL = "MODEEMPLOI_IMREC";
    private readonly DEFAULT_CGU_KEY = "CGU_IMREC";
    private readonly DEFAULT_PRIVACY_POLICY_KEY = "POL_IMREC";

    constructor(private attachmentWsDao: AttachmentWsDao,
                private dateProvider: DateProvider,
                private cacheService: CacheService,
                private serversConnectionsProvider: ServersConnectionsProvider) {
        super();
    }

    public async cacheByCompanyId(companyId: string) {
        await this.attachmentWsDao.getByCompanyId(this.serversConnectionsProvider.getServerConnection(), companyId);
    }

    public getByDamageId(damageId: string, attachmentKind?: AttachmentKinds): Promise<AttachmentDto[]> {
        return this.attachmentWsDao.getByDamageId(this.serversConnectionsProvider.getPrimaryServerConnection(), damageId, attachmentKind);
    }

    public async cacheDefaultUserManual() {
        let attachment = await this.attachmentWsDao.getById(this.serversConnectionsProvider.getPrimaryServerConnection(), this.DEFAULT_USER_MANUAL, this.DEFAULT_USER_MANUAL, this.DEFAULT_USER_MANUAL);
        await this.cacheService.cache(this.DEFAULT_USER_MANUAL, attachment.txt, this.dateProvider.now().plus({ years: 10 }));
    }

    public async cacheDefaultCGU() {
        let attachment = await this.attachmentWsDao.getById(this.serversConnectionsProvider.getPrimaryServerConnection(), this.DEFAULT_CGU_KEY, this.DEFAULT_CGU_KEY, this.DEFAULT_CGU_KEY);
        await this.cacheService.cache(this.DEFAULT_CGU_KEY, attachment.txt, this.dateProvider.now().plus({ years: 10 }));
    }

    public async cacheDefaultPrivacyPolicy() {
        let attachment = await this.attachmentWsDao.getById(this.serversConnectionsProvider.getPrimaryServerConnection(),
            this.DEFAULT_PRIVACY_POLICY_KEY,
            this.DEFAULT_PRIVACY_POLICY_KEY,
            this.DEFAULT_PRIVACY_POLICY_KEY);
        await this.cacheService.cache(this.DEFAULT_PRIVACY_POLICY_KEY, attachment.txt, this.dateProvider.now().plus({ years: 10 }));
    }

    public getByObjectAndKey(object: string, key: string) {
        return this.attachmentWsDao.getByObjectAndKey(this.serversConnectionsProvider.getPrimaryServerConnection(), object, key);
    }

    public async getCGU(companyId: string): Promise<string> {
        let cacheId = "company/" + companyId + "/" + AttachmentKinds.CGU;
        if (await this.cacheService.isCached(cacheId)) {
            return (await this.cacheService.getCached(cacheId)).value;
        } else if (await this.cacheService.isCached(this.DEFAULT_CGU_KEY)) {
            return (await this.cacheService.getCached(this.DEFAULT_CGU_KEY)).value;
        } else {
            return "";
        }
    }

    public async getPrivacyPolicy(companyId: string): Promise<string> {
        let cacheId = "company/" + companyId + "/" + AttachmentKinds.POLITIQUE_CONFID;
        if (await this.cacheService.isCached(cacheId)) {
            return (await this.cacheService.getCached(cacheId)).value;
        } else if (await this.cacheService.isCached(this.DEFAULT_PRIVACY_POLICY_KEY)) {
            return (await this.cacheService.getCached(this.DEFAULT_PRIVACY_POLICY_KEY)).value;
        } else {
            return "";
        }
    }

    public async getUserManual(companyId: string): Promise<string> {
        let cacheId = "company/" + companyId + "/" + AttachmentKinds.MODE_EMPLOI;
        if (await this.cacheService.isCached(cacheId)) {
            return (await this.cacheService.getCached(cacheId)).value;
        } else if (await this.cacheService.isCached(this.DEFAULT_USER_MANUAL)) {
            return (await this.cacheService.getCached(this.DEFAULT_USER_MANUAL)).value;
        } else {
            return "";
        }
    }

    public async getCGL(companyId: string): Promise<string> {
        let cacheId = "company/" + companyId + "/" + AttachmentKinds.CGL;
        if (await this.cacheService.isCached(cacheId)) {
            return (await this.cacheService.getCached(cacheId)).value;
        } else {
            return "";
        }
    }
}
